import { useState, useEffect, useCallback } from 'react';

const WORKER_URL = 'https://swgapibank.koevetmc.workers.dev';

export interface TradingItem {
  _oid: string;
  _className: string;
  objectName?: {
    stringID: string;
  };
  Consumable?: {
    buffName: string;
    modifiers: Array<{ [key: string]: number }>;
  };
  playerName: string;
  playerOID: number;
  displayedName?: string; // Localized display name from server (e.g., "Crafter's Pants")
  customName?: string; // Player-renamed items
  displayName?: string; // Normalized display name (added by hook)
  stats?: {
    condition?: number; // Current condition
    maxCondition?: number; // Maximum condition
    conditionPercent?: number; // Condition as percentage (0-100)
    useCount?: number; // Remaining uses (for consumables, tools, etc.)
    level?: number; // Item level
    volume?: number; // Item volume
    complexity?: number; // Crafting complexity
    crafter?: string; // Name of the crafter
  };
  // Add any other item properties you need
  [key: string]: any; // Allow any other properties
}

export interface WhisperResult {
  success: boolean;
  error?: string;
}

export interface User {
  username: string; // Account username
  characterName: string; // First character name (used for whispers)
}

export interface UseOnlinePlayerItemsResult {
  items: TradingItem[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  // Auth functions - all built into the hook
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  // Whisper function - automatically uses logged-in user
  whisperItem: (item: TradingItem, customMessage?: string) => Promise<WhisperResult>;
}

/**
 * Helper function to extract item display name from various possible locations
 */
export function getItemDisplayName(item: TradingItem): string {
  // Try displayedName from server FIRST (localized name that players see in-game)
  // This ensures consistency - food items show proper names like "Thakitillo" instead of "food.dish_thakitillo"
  // This is the most accurate name (e.g., "Crafter's Pants" instead of "Pants S29")
  if (item.displayedName) {
    return item.displayedName;
  }
  
  // Fallback to Consumable buffName only if displayedName is not available
  if (item.Consumable?.buffName) {
    return item.Consumable.buffName;
  }
  
  // Try customName if available (player-renamed items)
  if (item.customName) {
    return item.customName;
  }
  
  // Try objectName.stringID (localization key, less accurate)
  if (item.objectName?.stringID) {
    return item.objectName.stringID;
  }
  
  // Try _templateObject (contains the actual template path with item name)
  // Example: "object/tangible/survey_tool/survey_tool_mineral.iff" -> "Survey Tool Mineral"
  if (item._templateObject) {
    const templateMatch = item._templateObject.match(/([^/]+)\.iff?$/);
    if (templateMatch) {
      const templateName = templateMatch[1];
      // Convert "survey_tool_mineral" to "Survey Tool Mineral"
      return templateName
        .replace(/_/g, ' ') // Replace underscores with spaces
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
        .join(' ');
    }
  }
  
  // Try objectTemplate (alternative field name)
  if (item.objectTemplate) {
    const templateMatch = item.objectTemplate.match(/([^/]+)\.iff?$/);
    if (templateMatch) {
      const templateName = templateMatch[1];
      return templateName
        .replace(/_/g, ' ')
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
  }
  
  // Try templateName if available
  if (item.templateName) {
    return item.templateName;
  }
  
  // Fallback to className (but format it better)
  if (item._className) {
    // Convert "CraftingTool" to "Crafting Tool" for better display
    return item._className.replace(/([A-Z])/g, ' $1').trim();
  }
  
  return 'Unknown Item';
}

const STORAGE_KEY = 'swg_trading_auth';

/**
 * Simple hook to get all items from online players' banks
 * Includes built-in authentication and whisper functionality
 * Just use this hook and style the data however you want!
 */
export function useOnlinePlayerItems(): UseOnlinePlayerItemsResult {
  const [items, setItems] = useState<TradingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Auth state - built into the hook
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get online players (add timestamp to prevent caching)
      const timestamp = Date.now();
      const playersRes = await fetch(`${WORKER_URL}/online-players?_t=${timestamp}`);
      if (!playersRes.ok) {
        const errorText = await playersRes.text();
        throw new Error(`Failed to fetch online players: ${playersRes.status} - ${errorText}`);
      }
      const playersData = await playersRes.json();
      
      console.log('Online players:', playersData.players?.length || 0);
      // Fetch each player's bank
      const allItems: TradingItem[] = [];
      
      for (const player of playersData.players || []) {
        try {
          // Add timestamp to prevent browser caching
          const timestamp = Date.now();
          const bankRes = await fetch(`${WORKER_URL}/bank?playerName=${encodeURIComponent(player.name)}&_t=${timestamp}`);
          if (!bankRes.ok) continue; // Skip if bank fetch fails
          
          const bankData = await bankRes.json();
          
          console.log(`Bank data for ${player.name}:`, bankData);
          
          // Extract items from bank
          // Items are stored in bankData.bank with OIDs as keys
          if (bankData.bank) {
            // Find the bank container (usually has objectName.stringID === 'bank')
            let bankContainerOid: string | null = null;
            for (const oid in bankData.bank) {
              const obj = bankData.bank[oid];
              if (obj?.objectName?.stringID === 'bank' || obj?._className === 'Container') {
                bankContainerOid = oid;
                break;
              }
            }
            
            // Extract all items (skip the bank container itself)
            for (const oid in bankData.bank) {
              const item = bankData.bank[oid];
              
              // Skip the bank container itself
              if (oid === bankContainerOid) continue;
              
              // Skip nested containers for now (can add later if needed)
              if (item?._className === 'Container') continue;
              
              // Add item with player info
              if (item && item._oid) {
                const tradingItem = {
                  ...item,
                  playerName: player.name,
                  playerOID: player.oid,
                } as TradingItem;
                
                // Add normalized display name for easy access
                tradingItem.displayName = getItemDisplayName(tradingItem);
                
                // Debug logging for CraftingTool and SurveyTool items
                if (tradingItem._className === 'CraftingTool' || tradingItem._className === 'SurveyTool') {
                  console.log(`=== ${tradingItem._className} Item Debug ===`);
                  console.log('Full item object:', tradingItem);
                  console.log('objectName:', tradingItem.objectName);
                  console.log('objectTemplate:', tradingItem.objectTemplate);
                  console.log('customName:', tradingItem.customName);
                  console.log('displayName result:', tradingItem.displayName);
                  console.log('All keys:', Object.keys(tradingItem));
                  console.log('========================');
                }
                
                allItems.push(tradingItem);
              }
            }
          }
          
          // Small delay to avoid overwhelming API
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (err) {
          // Continue with other players if one fails
          console.error(`Failed to fetch bank for ${player.name}:`, err);
        }
      }
      console.log(`Total items found: ${allItems.length}`);
      setItems(allItems);
    } catch (err: any) {
      console.error('Error fetching items:', err);
      setError(err.message || 'Failed to fetch items');
    } finally {
      setLoading(false);
    }
  };

  // Load saved auth state from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const userData = JSON.parse(saved);
        setUser(userData);
      }
    } catch (err) {
      console.error('Failed to load saved auth:', err);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Login function - authenticates with SWG server using account username/password
  // Automatically fetches the first character name for whispers
  const login = useCallback(async (username: string, password: string) => {
    setAuthLoading(true);
    setError(null);
    try {
      // Authenticate with SWG server via Cloudflare Worker
      const response = await fetch(`${WORKER_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Login failed' }));
        throw new Error(errorData.error || `Login failed: ${response.status}`);
      }
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Login failed');
      }
      // Get character name from login response (automatically fetched by server)
      let characterName = data.characterName;
      
      // If not in response, try to fetch it
      if (!characterName) {
        try {
          const charsResponse = await fetch(`${WORKER_URL}/account/${encodeURIComponent(username)}/character`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          if (charsResponse.ok) {
            const charsData = await charsResponse.json();
            characterName = charsData.characterName || charsData.firstName;
          }
        } catch (err) {
          console.warn('Failed to fetch character name:', err);
        }
      }
      if (!characterName) {
        throw new Error('Could not find character name for account. Please ensure your account has at least one character.');
      }
      // Store authenticated user (account username and character name)
      // Use first name only for consistency
      const characterFirstName = characterName.trim().split(' ')[0];
      const userData: User = {
        username,
        characterName: characterFirstName,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
      setUser(userData);
    } catch (err: any) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Logout function - built into the hook
  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
    setError(null);
  }, []);

  // Function to send whisper about an item - automatically uses logged-in user
  const whisperItem = useCallback(async (
    item: TradingItem,
    customMessage?: string
  ): Promise<WhisperResult> => {
    if (!user) {
      return {
        success: false,
        error: 'You must be logged in to send whispers',
      };
    }
    // Prevent users from whispering themselves
    // Compare character name to player name (case-insensitive)
    const playerFirstName = item.playerName.split(' ')[0].trim();
    if (user.characterName.toLowerCase() === playerFirstName.toLowerCase()) {
      return {
        success: false,
        error: 'You cannot whisper yourself',
      };
    }
    try {
      const defaultMessage = `I would like to buy your ${item.displayName || 'item'}.`;
      const message = customMessage || defaultMessage;
      // Extract first name only (API expects first name, not full name)
      // Player names might be "FirstName LastName" or just "FirstName"
      const playerFirstName = item.playerName.split(' ')[0].trim();
      console.log('Sending whisper:', {
        from: user.characterName,
        to: playerFirstName,
        originalPlayerName: item.playerName,
        message: message,
      });
      const response = await fetch(`${WORKER_URL}/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from: user.characterName, // Use character name for whispers (so recipient can reply)
          to: playerFirstName, // Use first name only (API requirement)
          body: message,
        }),
      });
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        // Response might not be JSON
        return {
          success: false,
          error: `Server error: ${response.status} - ${responseText}`,
        };
      }
      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.action_error || `Failed to send whisper: ${response.status} - ${responseText}`,
        };
      }
      
      if (data.action_result === 'SUCCESS') {
        console.log('Whisper sent successfully');
        return { success: true };
      } else {
        return {
          success: false,
          error: data.action_error || data.error || 'Failed to send whisper',
        };
      }
    } catch (err: any) {
      console.error('Whisper error:', err);
      return {
        success: false,
        error: err.message || 'Failed to send whisper',
      };
    }
  }, [user]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 10 seconds (faster updates)
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  return { 
    items, 
    loading: loading || authLoading, 
    error, 
    refresh: fetchData, 
    // Auth - all built into the hook
    user,
    isAuthenticated: !!user,
    login,
    logout,
    // Whisper - automatically uses logged-in user
    whisperItem,
  };
}
